<template>
  <div
    ref="mobileNav"
    class="account-mobile-nav"
    :class="{ 'has-scroll-left': canScrollLeft, 'has-scroll-right': canScrollRight }"
  >
    <button
      v-if="canScrollLeft"
      type="button"
      class="account-mobile-nav-arrow account-mobile-nav-arrow--left"
      :aria-label="$t('components.chat.scroll_options_left')"
      :title="$t('components.chat.scroll_options_left')"
      @click="scrollNav(-1)"
    >
      <el-icon aria-hidden="true"><ArrowLeft /></el-icon>
    </button>
    <div
      ref="mobileNavScroller"
      class="account-mobile-nav-scroller"
      @scroll.passive="updateScrollState"
    >
      <button
        v-for="menuItem in menuItems"
        :key="menuItem.value"
        type="button"
        class="mobile-nav-item"
        :class="activeMenu === menuItem.value ? 'active' : ''"
        :data-menu="menuItem.value"
        :aria-current="activeMenu === menuItem.value ? 'true' : null"
        @click="$emit('select', menuItem.value)"
      >{{ menuItem.label }}</button>
    </div>
    <button
      v-if="canScrollRight"
      type="button"
      class="account-mobile-nav-arrow account-mobile-nav-arrow--right"
      :aria-label="$t('components.chat.scroll_options_right')"
      :title="$t('components.chat.scroll_options_right')"
      @click="scrollNav(1)"
    >
      <el-icon aria-hidden="true"><ArrowRight /></el-icon>
    </button>
  </div>
</template>

<script>
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue'

const NAV_EDGE_SAFE_WIDTH = 8

export default {
  name: 'MobileAccountNav',
  components: { ArrowLeft, ArrowRight },
  props: {
    menuItems: { type: Array, required: true },
    activeMenu: { type: String, required: true }
  },
  emits: ['select'],
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      scrollStateFrame: null
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.$nextTick(this.scrollActiveNavIntoView)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.cancelScrollState()
  },
  updated() {
    this.scheduleScrollState()
  },
  methods: {
    getScrollContainer() {
      return this.$refs.mobileNavScroller
    },
    handleResize() {
      this.scrollActiveNavIntoView()
    },
    scheduleScrollState() {
      if (this.scrollStateFrame !== null || typeof window === 'undefined') return
      if (typeof window.requestAnimationFrame !== 'function') {
        this.updateScrollState()
        return
      }
      this.scrollStateFrame = window.requestAnimationFrame(() => {
        this.scrollStateFrame = null
        this.updateScrollState()
      })
    },
    cancelScrollState() {
      if (this.scrollStateFrame === null) return
      if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(this.scrollStateFrame)
      }
      this.scrollStateFrame = null
    },
    updateScrollState() {
      const nav = this.getScrollContainer()
      if (!nav) return
      const maxScrollLeft = Math.max(0, nav.scrollWidth - nav.clientWidth)
      const scrollLeft = Math.max(0, nav.scrollLeft)
      const availableWidth = this.$refs.mobileNav?.clientWidth || nav.clientWidth
      const hasOverflow = nav.scrollWidth - availableWidth > 1
      this.canScrollLeft = hasOverflow && scrollLeft > 1
      this.canScrollRight = hasOverflow && scrollLeft < maxScrollLeft - 1
    },
    scrollNav(direction) {
      const nav = this.getScrollContainer()
      if (!nav || !direction) return
      const distance = Math.max(120, Math.floor(nav.clientWidth * 0.72))
      nav.scrollBy({ left: direction * distance, behavior: 'smooth' })
      this.scheduleScrollState()
    },
    // 只滚动导航本身；平板转为纵向导航后仍需让选中项可见，避免牵动主内容区和焦点。
    scrollActiveNavIntoView() {
      const activeMenu = this.activeMenu
      this.updateScrollState()
      this.$nextTick(() => {
        if (this.activeMenu !== activeMenu || !this.revealActiveNavItem()) return
        this.$nextTick(() => {
          if (this.activeMenu === activeMenu) this.revealActiveNavItem()
        })
      })
    },
    revealActiveNavItem() {
      const nav = this.getScrollContainer()
      if (!nav) return false
      const activeItem = nav.querySelector(`.mobile-nav-item[data-menu="${this.activeMenu}"]`)
      if (!activeItem) return false
      const navRect = nav.getBoundingClientRect()
      const itemRect = activeItem.getBoundingClientRect()
      const left = navRect.left + nav.clientLeft
      const top = navRect.top + nav.clientTop
      let scrollLeft = 0
      let scrollTop = 0
      if (nav.scrollWidth > nav.clientWidth) {
        const visibleLeft = left + (this.canScrollLeft ? NAV_EDGE_SAFE_WIDTH : 0)
        const visibleRight = left + nav.clientWidth - (this.canScrollRight ? NAV_EDGE_SAFE_WIDTH : 0)
        if (itemRect.left < visibleLeft) scrollLeft = itemRect.left - visibleLeft
        else if (itemRect.right > visibleRight) scrollLeft = itemRect.right - visibleRight
      }
      if (nav.scrollHeight > nav.clientHeight) {
        if (itemRect.top < top) scrollTop = itemRect.top - top
        else if (itemRect.bottom > top + nav.clientHeight) scrollTop = itemRect.bottom - top - nav.clientHeight
      }
      if (scrollLeft || scrollTop) {
        nav.scrollBy({ left: scrollLeft, top: scrollTop, behavior: scrollLeft ? 'auto' : 'smooth' })
      }
      if (scrollLeft) this.updateScrollState()
      else this.scheduleScrollState()
      return Boolean(scrollLeft)
    }
  }
}
</script>
